// src/components/FarmerNegotiationsTab.tsx - SCRIPT COMPLET CORRIGÉ
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import negotiationService, { 
  PriceNegotiation, 
  PriceNegotiationResponse
} from '../services/negotiationService';
import productService from '../services/productService';
import { 
  MessageCircle, 
  DollarSign, 
  Check, 
  X, 
  Handshake,
  Clock,
  User,
  Package,
  AlertCircle,
  RefreshCw,
  CheckCircle
} from 'lucide-react';

const FarmerNegotiationsTab = () => {
  const { user } = useAuth();
  
  const [negotiations, setNegotiations] = useState<PriceNegotiation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // États pour les actions
  const [respondingTo, setRespondingTo] = useState<number | null>(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedNegotiation, setSelectedNegotiation] = useState<PriceNegotiation | null>(null);
  const [responseAction, setResponseAction] = useState<'accept' | 'reject' | 'counter_offer'>('accept');
  const [counterOfferPrice, setCounterOfferPrice] = useState('');
  
  // Cache des produits
  const [products, setProducts] = useState<{[key: number]: any}>({});

  useEffect(() => {
    if (user?.is_farmer) {
      loadNegotiations();
    }
  }, [user, statusFilter]);

  const loadNegotiations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const negotiationsData = await negotiationService.getFarmerNegotiations(statusFilter);
      setNegotiations(negotiationsData);
      
      // Charger les détails des produits
      const productIds = [...new Set(negotiationsData.map(n => n.product_id))];
      const productPromises = productIds.map(id => 
        productService.getProduct(id).catch(() => null)
      );
      
      const productResults = await Promise.all(productPromises);
      const productMap: {[key: number]: any} = {};
      
      productResults.forEach((product, index) => {
        if (product) {
          productMap[productIds[index]] = product;
        }
      });
      
      setProducts(productMap);
      
    } catch (error) {
      console.error('Erreur lors du chargement des négociations:', error);
      setError('Erreur lors du chargement des négociations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenResponseModal = (negotiation: PriceNegotiation) => {
    setSelectedNegotiation(negotiation);
    setResponseAction('accept');
    setCounterOfferPrice('');
    setShowResponseModal(true);
  };

  const handleSubmitResponse = async () => {
    if (!selectedNegotiation) return;

    // Validation pour contre-offre
    if (responseAction === 'counter_offer') {
      const counterPrice = parseFloat(counterOfferPrice);
      const errors = negotiationService.validateCounterOffer(
        counterPrice,
        selectedNegotiation.proposed_price,
        selectedNegotiation.original_price
      );
      
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }
    }

    try {
      setRespondingTo(selectedNegotiation.id);
      setError('');
      setSuccess('');
      
      const response: PriceNegotiationResponse = {
        action: responseAction,
        counter_offer_price: responseAction === 'counter_offer' ? parseFloat(counterOfferPrice) : undefined
      };
      
      const result = await negotiationService.respondToNegotiation(selectedNegotiation.id, response);
      
      // Recharger les données
      await loadNegotiations();
      
      // ✅ CORRECTION - Utiliser la bonne valeur pour le message de succès
      let successMessage = '';
      switch (responseAction) {
        case 'accept':
          successMessage = `Proposition acceptée ! Prix final : ${negotiationService.formatPrice(selectedNegotiation.proposed_price)}`;
          break;
        case 'reject':
          successMessage = 'Proposition refusée';
          break;
        case 'counter_offer':
          // ✅ Utiliser la valeur du formulaire au lieu de selectedNegotiation
          successMessage = `Contre-offre envoyée : ${negotiationService.formatPrice(parseFloat(counterOfferPrice))}`;
          break;
        default:
          successMessage = 'Action effectuée';
      }
      
      setSuccess(successMessage);
      setShowResponseModal(false);
      setSelectedNegotiation(null);
      
    } catch (error: any) {
      console.error('Erreur lors de la réponse:', error);
      
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Erreur lors de l\'envoi de la réponse. Veuillez réessayer.');
      }
    } finally {
      setRespondingTo(null);
    }
  };

  const calculateDiscount = (originalPrice: number, proposedPrice: number): number => {
    return ((originalPrice - proposedPrice) / originalPrice) * 100;
  };

  if (!user?.is_farmer) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header avec filtre */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-slate">Négociations reçues</h3>
          <p className="text-gray-600 text-sm">Gérez les propositions de prix de vos clients</p>
        </div>
        <button
          onClick={loadNegotiations}
          className="bg-olive text-white px-3 py-2 rounded-md hover:bg-terracotta transition duration-300 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800 text-sm">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800 text-sm">{success}</span>
        </div>
      )}

      {/* Filtre */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-2">
              Filtrer par statut
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="counter_offered">Contre-offre envoyée</option>
              <option value="accepted">Acceptées</option>
              <option value="rejected">Refusées</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              {negotiations.length} négociation(s) au total
            </div>
          </div>
        </div>
      </div>

      {/* Liste des négociations */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-olive mx-auto mb-2" />
          <p>Chargement des négociations...</p>
        </div>
      ) : negotiations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate mb-2">Aucune négociation</h3>
          <p className="text-gray-600">
            {statusFilter ? 'Aucune négociation trouvée avec ce filtre.' : 'Vous n\'avez pas encore reçu de propositions de prix.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {negotiations.map((negotiation) => {
            const product = products[negotiation.product_id];
            const discount = calculateDiscount(negotiation.original_price, negotiation.proposed_price);
            
            return (
              <div key={negotiation.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-slate">
                        {product?.name || `Produit #${negotiation.product_id}`}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        negotiationService.getStatusColor(negotiation.status)
                      }`}>
                        {negotiationService.getStatusLabel(negotiation.status)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        <span>Client #{negotiation.customer_id}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Package className="h-4 w-4" />
                        <span>{negotiation.quantity} unité(s)</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{negotiationService.formatDate(negotiation.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {negotiation.status === 'pending' && (
                    <button
                      onClick={() => handleOpenResponseModal(negotiation)}
                      className="bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition duration-300 flex items-center gap-2"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Répondre
                    </button>
                  )}
                </div>

                {/* Détails des prix */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 rounded p-3">
                    <div className="text-sm text-gray-600">Prix original</div>
                    <div className="text-lg font-semibold text-slate">
                      {negotiationService.formatPrice(negotiation.original_price)}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded p-3">
                    <div className="text-sm text-blue-600">Prix proposé</div>
                    <div className="text-lg font-semibold text-blue-800">
                      {negotiationService.formatPrice(negotiation.proposed_price)}
                      <div className="text-xs text-red-600">
                        -{discount.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  
                  {negotiation.counter_offer_price && (
                    <div className="bg-green-50 rounded p-3">
                      <div className="text-sm text-green-600">Votre contre-offre</div>
                      <div className="text-lg font-semibold text-green-800">
                        {negotiationService.formatPrice(negotiation.counter_offer_price)}
                        <div className="text-xs text-red-600">
                          -{calculateDiscount(negotiation.original_price, negotiation.counter_offer_price).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Calcul total */}
                <div className="bg-terracotta bg-opacity-10 rounded p-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate font-medium">
                      Total proposé ({negotiation.quantity} unité{negotiation.quantity > 1 ? 's' : ''})
                    </span>
                    <span className="text-xl font-bold text-terracotta">
                      {negotiationService.formatPrice(negotiation.proposed_price * negotiation.quantity)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    vs {negotiationService.formatPrice(negotiation.original_price * negotiation.quantity)} au prix original
                  </div>
                </div>

                {/* Informations supplémentaires */}
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <div>
                    Proposition {negotiation.customer_proposals_count}/3 du client
                  </div>
                  <div>
                    {negotiationService.isActive(negotiation) ? (
                      <span className="text-yellow-600">
                        Expire {negotiationService.getTimeUntilExpiration(negotiation)}
                      </span>
                    ) : (
                      <span>
                        {negotiation.status === 'accepted' ? 'Acceptée' : 
                         negotiation.status === 'rejected' ? 'Refusée' : 
                         'Terminée'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de réponse */}
      {showResponseModal && selectedNegotiation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate">
                  Répondre à la négociation
                </h3>
                <button
                  onClick={() => setShowResponseModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Résumé de la négociation */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-slate mb-2">
                  {products[selectedNegotiation.product_id]?.name || `Produit #${selectedNegotiation.product_id}`}
                </h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><span className="font-medium">Prix original:</span> {negotiationService.formatPrice(selectedNegotiation.original_price)}</p>
                  <p><span className="font-medium">Prix proposé:</span> {negotiationService.formatPrice(selectedNegotiation.proposed_price)}</p>
                  <p><span className="font-medium">Quantité:</span> {selectedNegotiation.quantity} unité(s)</p>
                  <p><span className="font-medium">Réduction demandée:</span> {calculateDiscount(selectedNegotiation.original_price, selectedNegotiation.proposed_price).toFixed(1)}%</p>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Votre réponse
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="response"
                        value="accept"
                        checked={responseAction === 'accept'}
                        onChange={(e) => setResponseAction(e.target.value as any)}
                        className="text-olive focus:ring-olive"
                      />
                      <Check className="h-4 w-4 text-green-600" />
                      <span>Accepter la proposition</span>
                    </label>
                    
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="response"
                        value="reject"
                        checked={responseAction === 'reject'}
                        onChange={(e) => setResponseAction(e.target.value as any)}
                        className="text-olive focus:ring-olive"
                      />
                      <X className="h-4 w-4 text-red-600" />
                      <span>Refuser la proposition</span>
                    </label>
                    
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="response"
                        value="counter_offer"
                        checked={responseAction === 'counter_offer'}
                        onChange={(e) => setResponseAction(e.target.value as any)}
                        className="text-olive focus:ring-olive"
                      />
                      <Handshake className="h-4 w-4 text-blue-600" />
                      <span>Faire une contre-offre</span>
                    </label>
                  </div>
                </div>

                {/* Champ contre-offre */}
                {responseAction === 'counter_offer' && (
                  <div>
                    <label className="block text-sm font-medium text-slate mb-2">
                      Prix de contre-offre (FCFA)
                    </label>
                    <input
                      type="number"
                      value={counterOfferPrice}
                      onChange={(e) => setCounterOfferPrice(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                      placeholder={`Entre ${selectedNegotiation.proposed_price} et ${selectedNegotiation.original_price}`}
                      min={selectedNegotiation.proposed_price}
                      max={selectedNegotiation.original_price}
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      Doit être entre {negotiationService.formatPrice(selectedNegotiation.proposed_price)} et {negotiationService.formatPrice(selectedNegotiation.original_price)}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowResponseModal(false)}
                  disabled={respondingTo === selectedNegotiation.id}
                  className="flex-1 bg-gray-200 text-slate px-4 py-2 rounded-md hover:bg-gray-300 transition duration-300 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmitResponse}
                  disabled={
                    respondingTo === selectedNegotiation.id || 
                    (responseAction === 'counter_offer' && (!counterOfferPrice || parseFloat(counterOfferPrice) <= selectedNegotiation.proposed_price || parseFloat(counterOfferPrice) >= selectedNegotiation.original_price))
                  }
                  className="flex-1 bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {respondingTo === selectedNegotiation.id ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-4 w-4" />
                      Envoyer la réponse
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmerNegotiationsTab;